import {
  CollapsedExpandedComponentRef,
  ComponentRef,
} from '@wix/editor-platform-sdk-types';
import {
  IContextBasedFactory,
  IDocumentServices,
  IHostType,
} from '../../common';
import {
  IHiddenComponentOptions,
  IRole,
  ISelector,
} from './refComponents.types';

import { memoize, minBy } from 'lodash';
import { CompRef } from '@wix/document-services-types';
import {
  BI_DEPRECATED_COMPLEX_SELECTOR,
  createBiLogger,
  reportBi,
  getEditorTypeForBI,
} from '../../bi';

export type ISuperAPI = {
  refComponents: {
    findComponentBySelector(
      rootComp: ComponentRef,
      selector: ISelector,
    ): CompRef;
    hideReferredComponent(compRef: ComponentRef): Promise<void>;
    showReferredComponent(compRef: ComponentRef): Promise<void>;
    hideBySelector(rootComp: ComponentRef, selector: ISelector): Promise<void>;
    showBySelector(rootComp: ComponentRef, selector: ISelector): Promise<void>;
    getHiddenComponents(
      rootComp: ComponentRef,
      options?: IHiddenComponentOptions,
    ): Promise<CollapsedExpandedComponentRef[]>;
    getHiddenComponentRoles(
      rootComp: ComponentRef,
      options?: IHiddenComponentOptions,
    ): Promise<IRole[]>;
    findHiddenComponentByRole(
      rootComp: ComponentRef,
      role: IRole,
      options?: IHiddenComponentOptions,
    ): Promise<ComponentRef | null>;
    isHiddenBySelector(
      rootComp: ComponentRef,
      selector: ISelector,
    ): Promise<boolean>;
  };
};

export type IHostAPI<T extends IHostType> = T extends IHostType ? {} : never;

type ComponentTreeItem = {
  componentRef: CompRef;
  role: string;
  children?: ComponentTreeItem[];
};

export function createSuperAPIFactory<T extends IHostType>(
  // NOTE: hostType is ignored in that case, however, it could be used to return a different SuperAPI per host
  // @ts-expect-error
  hostType: T,
  // @ts-expect-error
  hostAPI: IHostAPI,
  ds: IDocumentServices,
): IContextBasedFactory<ISuperAPI> {
  return (context) => {
    const appDefinitionId = context.appDefinitionId;

    try {
      createBiLogger({
        userId: ds.generalInfo.getUserInfo().userId,
        metaSiteId: ds.generalInfo.getMetaSiteId(),
        editorType: getEditorTypeForBI(),
      });
    } catch (e) {
      console.error('failed to create bi logger', e);
    }

    async function waitForChangesApplied() {
      return new Promise<void>((resolve) => ds.waitForChangesApplied(resolve));
    }

    function isRefComponent(compRef: CompRef): boolean {
      return (
        ds.components.getType(compRef) ===
        'wysiwyg.viewer.components.RefComponent'
      );
    }

    function validateReferredComponent(compRef: CompRef): void {
      if (!ds.components.refComponents.isReferredComponent(compRef)) {
        throw new CompIsNotAReferredCompError(compRef.id);
      }
      // // TODO: try to understand whether or not we need to check the parent comp
      const hostRef =
        ds.components.refComponents.getRefHostCompPointer(compRef);

      if (!hostRef) {
        // should never happen in fact if component passes "isReferredComponent" check
        throw new UnableToFindAnOwnerError(compRef.id);
      }

      if (
        ds.components.data.get(hostRef)?.appDefinitionId !==
        context.appDefinitionId
      ) {
        throw new NotAllowedToChangeRefComponent(compRef.id);
      }
    }

    function getGhostComponents(
      compRef: ComponentRef,
    ): Record<string, { role: string }> {
      return ds.components.refComponents.getAllGhostRefComponentsPrimaryConnection(
        compRef,
      );
    }

    function getFirstRefComponent(compRef: ComponentRef): ComponentRef {
      if (isRefComponent(compRef)) {
        return compRef;
      }

      const refComponent =
        ds.components.refComponents.getRefHostCompPointer(compRef);

      if (!refComponent) {
        throw new UnableToFindRefComponent(compRef.id);
      }

      return refComponent as ComponentRef;
    }

    const buildComponentTree = memoize(
      (componentRef: CompRef): ComponentTreeItem => {
        const connection =
          ds.platform.controllers.connections.getPrimaryConnection(
            componentRef,
          );

        return {
          componentRef,
          role: connection?.role,
          children: ds.deprecatedOldBadPerformanceApis.components
            .getChildren(componentRef)
            .map(buildComponentTree),
        };
      },
      (compRef) => compRef.id,
    );

    const findComponentBySelectorInternal = (
      item: ComponentTreeItem,
      selectors: string[],
      nestingLevel: number = 0,
    ): { item: ComponentTreeItem; nestingLevel: number }[] => {
      const topSelector = selectors[0];
      if (!topSelector) {
        return [];
      }

      if (topSelector === item.role) {
        selectors.shift();
      }

      if (selectors.length === 0) {
        return [{ item, nestingLevel }];
      }

      if (!item.children?.length) {
        return [];
      }

      const childResults = item.children?.map((child) => {
        return findComponentBySelectorInternal(
          child,
          selectors.slice(),
          nestingLevel++,
        );
      });
      return childResults.flatMap((x) => x);
    };

    const findComponentBySelector = (
      rootCompRef: ComponentRef,
      selector: string,
      allowComplexSelector: boolean = true,
    ) => {
      const roles = selector
        .split(' ')
        .map((x) => x.trim())
        .map((x) => x.slice(1));

      if (!allowComplexSelector && roles.length > 1) {
        reportBi(BI_DEPRECATED_COMPLEX_SELECTOR, {});
        console.warn(
          'Complex selectors are deprecated and will be removed in the future. Please use getNested functionality instead.',
        );
      }
      const componentTree = buildComponentTree(rootCompRef);

      const foundElements = findComponentBySelectorInternal(
        componentTree,
        roles,
      );

      return minBy(foundElements, (x) => x.nestingLevel)?.item;
    };

    class RefComponentsSuperAPI {
      findComponentBySelector(compRef: ComponentRef, selector: ISelector) {
        const component = findComponentBySelector(compRef, selector);
        if (!component) {
          throw new UnableToFindComponentBySelector(selector);
        }
        return component.componentRef;
      }

      async hideReferredComponent(compRef: CompRef) {
        validateReferredComponent(compRef);
        ds.runInContext(appDefinitionId, () => {
          return ds.components.remove(compRef);
        });
        await waitForChangesApplied();
      }

      async showReferredComponent(compRef: CompRef) {
        validateReferredComponent(compRef);
        ds.runInContext(appDefinitionId, () => {
          return ds.components.refComponents.unGhostifyComponent(compRef);
        });
        await waitForChangesApplied();
      }

      async hideBySelector(rootComp: ComponentRef, selector: ISelector) {
        const componentToHide = findComponentBySelector(
          rootComp,
          selector,
          false,
        );
        if (!componentToHide) {
          throw new UnableToFindComponentBySelector(selector);
        }
        await this.hideReferredComponent(componentToHide.componentRef);
      }

      async showBySelector(rootComp: ComponentRef, selector: ISelector) {
        const componentToShow = findComponentBySelector(
          rootComp,
          selector,
          false,
        );
        if (!componentToShow) {
          throw new UnableToFindComponentBySelector(selector);
        }
        await this.showReferredComponent(componentToShow.componentRef);
      }

      async getHiddenComponents(
        refComponentRef: ComponentRef,
        options?: IHiddenComponentOptions,
      ) {
        if (!isRefComponent(refComponentRef)) {
          throw new UnableToGetHiddenNoneRefComponents(refComponentRef.id);
        }

        const refComponents = [refComponentRef];

        if (options?.includeNested) {
          const children =
            ds.deprecatedOldBadPerformanceApis.components.getChildren(
              refComponentRef,
              true,
            );
          children.forEach((compRef) => {
            if (isRefComponent(compRef as ComponentRef)) {
              refComponents.push(compRef as ComponentRef);
            }
          });
        }

        return refComponents.reduce<CollapsedExpandedComponentRef[]>(
          (acc, compRef) => {
            const ghostsComponents = getGhostComponents(compRef);

            if (ghostsComponents) {
              Object.keys(ghostsComponents).forEach((ghostId) => {
                acc.push({
                  componentRef: ds.components.get.byId(ghostId) as ComponentRef,
                  role: ghostsComponents[ghostId]?.role,
                });
              });
            }
            return acc;
          },
          [],
        );
      }

      async getHiddenComponentRoles(
        rootComp: ComponentRef,
        options?: IHiddenComponentOptions,
      ) {
        const refComponent = getFirstRefComponent(rootComp);
        const hiddenComponents = await this.getHiddenComponents(
          refComponent,
          options,
        );
        return hiddenComponents.map((hiddenComponent) => hiddenComponent.role);
      }

      async findHiddenComponentByRole(
        rootComp: ComponentRef,
        role: IRole,
        options?: IHiddenComponentOptions,
      ) {
        const refComponent = getFirstRefComponent(rootComp);
        const hiddenComponents = await this.getHiddenComponents(
          refComponent,
          options,
        );
        const hiddenComponent = hiddenComponents.find(
          (component) => component.role === role,
        );
        return hiddenComponent?.componentRef || null;
      }

      async isHiddenBySelector(rootComp: ComponentRef, selector: ISelector) {
        const bySelectorComponent = findComponentBySelector(rootComp, selector);
        if (!bySelectorComponent) {
          return false;
        }

        const allHiddenComponents = await this.getHiddenComponentRoles(
          rootComp,
          {
            includeNested: true,
          },
        );
        return allHiddenComponents.some(
          (hiddenComponent) => hiddenComponent === bySelectorComponent.role,
        );
      }
    }

    return {
      refComponents: new RefComponentsSuperAPI(),
    };
  };
}

export class CompIsNotAReferredCompError extends Error {
  constructor(compId: string) {
    super(`The component with id ${compId} isn't a referred component`);
  }
}

export class UnableToFindAnOwnerError extends Error {
  constructor(compId: string) {
    super(`Unable to find an an owner of component with id ${compId}`);
  }
}

export class NotAllowedToChangeRefComponent extends Error {
  constructor(compId: string) {
    super(`The component with id '${compId}' doesn't belong to this app.`);
  }
}

export class UnableToFindComponentBySelector extends Error {
  constructor(selector: ISelector) {
    super(`Unable to find component by selector: ${selector}`);
  }
}

export class UnableToGetHiddenNoneRefComponents extends Error {
  constructor(compId: string) {
    super(
      `Unable to find hidden component's for component with id ${compId}, the component is not a ref component`,
    );
  }
}

export class UnableToFindRefComponent extends Error {
  constructor(rootCompId: string) {
    super(`Unable to find refComponent for ${rootCompId}`);
  }
}
